import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  MapPin,
  Phone,
  MessageSquare,
  Copy,
  Sparkles
} from 'lucide-react';

export interface SharedProposalData {
  address?: string;
  clientName?: string;
  clientPhone?: string;
  capacityKw?: number;
  substationName?: string;
  dlName?: string;
}

interface AdminProposalPageProps {
  initialData?: SharedProposalData;
  onBackToHome?: () => void;
  onBackToPortal: () => void;
  onNavigateSubpage: (page: 'admin-portal' | 'admin-proposal' | 'admin-diagnostic' | 'admin-golden-targets' | 'admin-map') => void;
}

export const AdminProposalPage: React.FC<AdminProposalPageProps> = ({
  initialData,
  onBackToHome,
  onBackToPortal,
  onNavigateSubpage,
}) => {
  const [clientName, setClientName] = useState(initialData?.clientName || '');
  const [clientPhone, setClientPhone] = useState(initialData?.clientPhone || '');
  const [address, setAddress] = useState(initialData?.address || '');
  const [capacityKw, setCapacityKw] = useState<number>(initialData?.capacityKw || 250); // 설치 용량 kW 직접 입력 (기본 250kW)
  const [pricePerKw, setPricePerKw] = useState<number>(50000); // 1kW당 연간 수익 (기본 5만원)
  const [agentName, setAgentName] = useState('신동현 팀장');
  const [agentPhone, setAgentPhone] = useState('010-2767-1449');
  const [copied, setCopied] = useState(false);
  const [smsCopied, setSmsCopied] = useState(false);

  // Sync state whenever initialData changes from Grid Diagnostic or Golden Target
  useEffect(() => {
    if (initialData) {
      if (initialData.address !== undefined) setAddress(initialData.address);
      if (initialData.clientName !== undefined) setClientName(initialData.clientName);
      if (initialData.clientPhone !== undefined) setClientPhone(initialData.clientPhone);
      if (initialData.capacityKw !== undefined) setCapacityKw(initialData.capacityKw);
    }
  }, [initialData]);

  // Calculations (kW 직접 입력 기준, 1kW당 약 2평 환산)
  const estimatedPyung = capacityKw * 2;
  const annualIncomeWon = capacityKw * pricePerKw;
  const twentyYearIncomeWon = annualIncomeWon * 20;
  const twentyYearIncomeMillion = (twentyYearIncomeWon / 100000000).toFixed(1);

  const displayClientName = clientName.trim() || '고객님';
  const displayAddress = address.trim() || '설치 대상 주소 미지정';

  const handleCopyKakaoText = () => {
    const text = `[+SD 플러스에스디 공식 1:1 맞춤 지붕임대 견적서]

■ 수신: ${displayClientName}
■ 대상 주소: ${displayAddress}
■ 발전 설비: ${capacityKw.toLocaleString()} kW (지붕 약 ${estimatedPyung.toLocaleString()}평 / 1kW당 ${(pricePerKw / 10000).toLocaleString()}만 원)

[확정 임대수익 및 지원 혜택 요약]
1. 20년 총 확정 임대료: ${twentyYearIncomeMillion}억 원 (${(annualIncomeWon / 10000).toLocaleString()}만 원/년)
2. 지붕 수리비 지원: 노후 방수 및 보수 공사비 전폭 지원 (자부담 0원)
3. 한전 계통 연계: 22.9kV 배전선로 우선 인입 및 사전 인허가 보증
4. 건물주 투자금: 0원 (설치비/유지보수비 100% 전액 사업자 부담)

담당 수석컨설턴트: ${agentName} (${agentPhone})
상담 문의 바로가기: https://plussd.com/`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopySms = () => {
    const sms = `[+SD 지붕임대 견적서] ${displayClientName}
- 대상 주소: ${displayAddress}
- 발전설비: ${capacityKw}kW (지붕 약 ${estimatedPyung}평 / 1kW당 ${(pricePerKw / 10000).toLocaleString()}만원)
- 20년 확정수익: ${twentyYearIncomeMillion}억원 (연 ${(annualIncomeWon / 10000).toLocaleString()}만원)
- 지붕 수리비 전폭 지원 및 자부담 0원
담당자: ${agentName} (${agentPhone})`;

    navigator.clipboard.writeText(sms);
    setSmsCopied(true);
    setTimeout(() => setSmsCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-500 selection:text-white flex flex-col">
      
      {/* 1. Master Top Bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 py-3 px-4 sm:px-6 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
            <button
              type="button"
              onClick={onBackToPortal}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-sm"
            >
              <ChevronLeft className="w-4 h-4 text-sky-600" />
              <span>포털 메인으로</span>
            </button>

            {onBackToHome && (
              <button
                type="button"
                onClick={onBackToHome}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-xs"
              >
                <span>메인 홈</span>
              </button>
            )}

            <div className="flex items-center gap-2 ml-1">
              <span className="text-sm font-black text-slate-900 tracking-tight">
                📄 간단 견적서 만들기
              </span>
            </div>
          </div>

          {/* Quick Subpage Switcher Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto text-xs">
            <button
              type="button"
              onClick={() => onNavigateSubpage('admin-proposal')}
              className="px-3 py-1.5 rounded-xl bg-sky-600 text-white font-bold cursor-pointer shadow-sm"
            >
              1. 간단 견적서 만들기
            </button>
            <button
              type="button"
              onClick={() => onNavigateSubpage('admin-diagnostic')}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
            >
              2. 계통 여부 확인
            </button>
            <button
              type="button"
              onClick={() => onNavigateSubpage('admin-golden-targets')}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
            >
              3. 영업 추천 지역
            </button>
            <button
              type="button"
              onClick={() => onNavigateSubpage('admin-map')}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
            >
              4. GIS 관제 맵
            </button>
          </div>

        </div>
      </header>

      {/* 2. Workspace: Left Form Controls + Right Real-time Mobile Proposal Card */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 w-full flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Input Form Settings (col-span-6) */}
          <div className="lg:col-span-6 space-y-6 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm">
            <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-black text-sky-600 uppercase tracking-wider">
                  PROPOSAL GENERATOR
                </span>
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 mt-0.5">
                  1초 모바일 제안서 생성기
                </h1>
              </div>
              <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-bold border border-sky-200">
                <Sparkles className="w-3.5 h-3.5" />
                <span>계통 연동 활성화</span>
              </div>
            </div>

            <div className="space-y-4 text-xs font-bold text-slate-700">
              
              {/* Client Name & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 text-slate-500">고객/업체명 (수신인)</label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="예: 대성정밀 (김태양 대표님)"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-bold text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-slate-500">고객 연락처</label>
                  <input
                    type="text"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    placeholder="예: 010-1234-5678"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-bold text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Target Address */}
              <div>
                <label className="block mb-1 text-slate-500">대상 건물 / 공장 주소</label>
                <div className="relative">
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="예: 경기도 화성시 팔탄면 율암리 45 또는 부산광역시 부산진구 부전동"
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-bold text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                  />
                  <MapPin className="w-4 h-4 text-sky-600 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              {/* Direct Capacity kW Input & Unit Price */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="bg-sky-50/60 p-3.5 rounded-2xl border border-sky-100 space-y-1.5">
                  <label className="block text-sky-900 font-black text-xs">
                    설치 용량 (kW 직접 입력)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step={10}
                      value={capacityKw}
                      onChange={(e) => setCapacityKw(Math.max(10, Number(e.target.value)))}
                      className="w-full px-3 py-2 rounded-xl bg-white border border-sky-200 text-sky-950 font-black text-base focus:outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-sky-600">
                      kW (약 {estimatedPyung}평)
                    </span>
                  </div>
                  <span className="text-[11px] text-sky-700 block">
                    * 1kW당 약 2평 지붕 면적 기준 자동 환산
                  </span>
                </div>

                <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200 space-y-1.5">
                  <label className="block text-slate-700 font-black text-xs">
                    1kW당 연간 임대 단가
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step={5000}
                      value={pricePerKw}
                      onChange={(e) => setPricePerKw(Math.max(10000, Number(e.target.value)))}
                      className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-black text-base focus:outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500">
                      원/년 (기본 5만 원)
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-500 block">
                    * 250kW 기준 연 {(250 * pricePerKw / 10000).toLocaleString()}만 원
                  </span>
                </div>
              </div>

              {/* Consultant Agent Settings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-slate-100">
                <div>
                  <label className="block mb-1 text-slate-500">담당자 성함/직함</label>
                  <input
                    type="text"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-bold text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-slate-500">담당자 직통 연락처</label>
                  <input
                    type="text"
                    value={agentPhone}
                    onChange={(e) => setAgentPhone(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-bold text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 flex flex-col sm:flex-row gap-2.5">
                <button
                  type="button"
                  onClick={handleCopyKakaoText}
                  className="flex-1 py-3.5 px-4 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs flex items-center justify-center gap-2 shadow-md shadow-amber-400/20 cursor-pointer transition-all active:scale-98"
                >
                  <MessageSquare className="w-4 h-4 fill-slate-950" />
                  <span>{copied ? '카카오톡 양식 복사완료!' : '카카오톡 견적 양식 복사'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopySms}
                  className="flex-1 py-3.5 px-4 rounded-2xl bg-sky-600 hover:bg-sky-500 text-white font-black text-xs flex items-center justify-center gap-2 shadow-md shadow-sky-600/20 cursor-pointer transition-all active:scale-98"
                >
                  <Copy className="w-4 h-4" />
                  <span>{smsCopied ? 'SMS 복사완료!' : '문자(SMS) 요약 복사'}</span>
                </button>
              </div>

            </div>
          </div>

          {/* Right Column: Live Mobile Preview Card (col-span-6) */}
          <div className="lg:col-span-6 flex justify-center">
            <div className="w-full max-w-sm bg-white border border-slate-200 rounded-3xl p-6 shadow-xl space-y-5 text-slate-900">
              
              {/* Mobile Card Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-md bg-slate-900 p-0.5 shadow flex items-center justify-center">
                    <img src="/plus_sd_logo.png" alt="+SD" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-xs font-black tracking-tight text-slate-900">
                    플러스에스디 공식 제안서
                  </span>
                </div>
                <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                  1:1 맞춤 견적
                </span>
              </div>

              {/* Client & Address Info Badge */}
              <div className="bg-sky-50 p-4 rounded-2xl border border-sky-100 space-y-1">
                <div className="text-xs font-bold text-sky-700">고객명 (수신)</div>
                <div className="text-base font-black text-slate-900">
                  {clientName.trim() ? (
                    clientName
                  ) : (
                    <span className="text-slate-400 font-normal text-sm">고객명을 입력해 주세요 (수신)</span>
                  )}
                </div>
                <div className="text-xs text-slate-600 flex items-center gap-1 pt-1">
                  <MapPin className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                  <span className="truncate">
                    {address.trim() ? (
                      address
                    ) : (
                      <span className="text-slate-400 font-normal">설치 대상 주소를 입력해 주세요</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Key Highlights Metric Box */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between text-xs border-b border-slate-200 pb-2">
                  <span className="text-slate-500 font-bold">발전 설비 용량</span>
                  <strong className="text-slate-900 font-black text-sm">
                    {capacityKw.toLocaleString()} kW <span className="text-[11px] text-slate-500 font-normal">(약 {estimatedPyung}평)</span>
                  </strong>
                </div>

                <div className="flex items-center justify-between text-xs border-b border-slate-200 pb-2">
                  <span className="text-slate-500 font-bold">연간 확정 임대료</span>
                  <strong className="text-sky-600 font-black text-sm">
                    {(annualIncomeWon / 10000).toLocaleString()}만 원 / 년
                  </strong>
                </div>

                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-slate-900 font-black text-xs">20년 총 확정 수익</span>
                  <strong className="text-base font-black text-emerald-600">
                    {twentyYearIncomeMillion}억 원
                  </strong>
                </div>
              </div>

              {/* 4 Essential Benefits List */}
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2 text-slate-700 font-medium">
                  <div className="h-4 w-4 rounded-full bg-emerald-100 text-emerald-700 font-black text-[10px] flex items-center justify-center shrink-0 mt-0.5">
                    ✓
                  </div>
                  <span><strong>지붕 수리비 전폭 지원:</strong> 노후 방수 및 지붕 보수 전액 지원</span>
                </div>
                <div className="flex items-start gap-2 text-slate-700 font-medium">
                  <div className="h-4 w-4 rounded-full bg-emerald-100 text-emerald-700 font-black text-[10px] flex items-center justify-center shrink-0 mt-0.5">
                    ✓
                  </div>
                  <span><strong>한전 22.9kV 배전계통 연계:</strong> 154kV 관할 인허가 보증</span>
                </div>
                <div className="flex items-start gap-2 text-slate-700 font-medium">
                  <div className="h-4 w-4 rounded-full bg-emerald-100 text-emerald-700 font-black text-[10px] flex items-center justify-center shrink-0 mt-0.5">
                    ✓
                  </div>
                  <span><strong>초기 투자금 0원:</strong> 시공비/유지보수비 100% 사업자 부담</span>
                </div>
              </div>

              {/* Consultant Footer */}
              <div className="border-t border-slate-100 pt-3 flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] text-slate-600 block">수석 컨설턴트</span>
                  <strong className="text-slate-900 font-black">{agentName}</strong>
                </div>
                <a
                  href={`tel:${agentPhone.replace(/[^0-9]/g, '')}`}
                  className="px-3 py-1.5 rounded-xl bg-slate-900 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm"
                >
                  <Phone className="w-3 h-3 text-sky-400" />
                  <span>{agentPhone}</span>
                </a>
              </div>

            </div>
          </div>

        </div>
      </main>

    </div>
  );
};
